import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateProfile } from "../features/auth/authThunks.js";
import ProfileHeader from "../assets/clinic-entrance.jpg";

import PageHeader from "../pages/PageHeader.jsx";
import "../styles/profile.css";

const Profile = () => {
  const dispatch = useDispatch();
  const { user, token, loading } = useSelector((state) => state.auth);

  const [isEditing, setIsEditing] = useState(false);
  const [selectedProfileImageFile, setSelectedProfileImageFile] = useState(null);
  const [profileForm, setProfileForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    if (!user) return;

    if (!isEditing) {
      setProfileForm({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        phone: user.phone || "",
      });
      setSelectedProfileImageFile(null);
    }
  }, [user, isEditing]);

  const profileImageUrl = useMemo(() => {
    return user?.profile_picture_url || "/default-avatar.png";
  }, [user]);

  const handleToggleEdit = () => {
    const nextIsEditing = !isEditing;
    setIsEditing(nextIsEditing);

    setProfileForm({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
      phone: user?.phone || "",
    });
    setSelectedProfileImageFile(null);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setProfileForm((previousForm) => ({
      ...previousForm,
      [name]: value,
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedProfileImageFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token) return;

    const formData = new FormData();
    formData.append("first_name", profileForm.first_name);
    formData.append("last_name", profileForm.last_name);
    formData.append("email", profileForm.email);
    formData.append("phone", profileForm.phone);

    if (selectedProfileImageFile) {
      formData.append("profile_picture", selectedProfileImageFile);
    }

    const actionResult = await dispatch(updateProfile({ formData, token }));

    if (updateProfile.fulfilled.match(actionResult)) {
      setIsEditing(false);
      setSelectedProfileImageFile(null);
    }
  };

  const fullName = `${profileForm.first_name} ${profileForm.last_name}`.trim();

  return (
    <div className="profile-page">
      <div className="profile-top">
        <PageHeader />
      </div>

      <div className="profile-container">
        {!user ? (
          <div className="profile-content">
            <h3>Mis Datos</h3>
            <p className="profile-empty">Cargando perfil…</p>
          </div>
        ) : (
          <>
            <div
              className="profile-header"
              style={{ backgroundImage: `url(${ProfileHeader})` }}
            />

            <div className="profile-picture">
              <img
                src={profileImageUrl}
                alt={`Foto de perfil de ${fullName || "usuario"}`}
              />

              <div className="profile-info">
                <h2 className="profile-name">{fullName || "-"}</h2>
                <p className="profile-bio">{user?.role || "-"}</p>
              </div>
            </div>

            <div className="profile-content">
              <h3>Mis Datos</h3>

              <form onSubmit={handleSubmit}>
                <div className="profile-fields">
                  <div className="field">
                    <label htmlFor="profile_first_name">Nombre</label>
                    {isEditing ? (
                      <input
                        id="profile_first_name"
                        type="text"
                        name="first_name"
                        value={profileForm.first_name}
                        onChange={handleChange}
                      />
                    ) : (
                      <p>{user?.first_name || "-"}</p>
                    )}
                  </div>

                  <div className="field">
                    <label htmlFor="profile_last_name">Apellido</label>
                    {isEditing ? (
                      <input
                        id="profile_last_name"
                        type="text"
                        name="last_name"
                        value={profileForm.last_name}
                        onChange={handleChange}
                      />
                    ) : (
                      <p>{user?.last_name || "-"}</p>
                    )}
                  </div>

                  <div className="field">
                    <label htmlFor="profile_email">Email</label>
                    {isEditing ? (
                      <input
                        id="profile_email"
                        type="email"
                        name="email"
                        value={profileForm.email}
                        onChange={handleChange}
                      />
                    ) : (
                      <p>{user?.email || "-"}</p>
                    )}
                  </div>

                  <div className="field">
                    <label htmlFor="profile_phone">Teléfono</label>
                    {isEditing ? (
                      <input
                        id="profile_phone"
                        type="text"
                        name="phone"
                        value={profileForm.phone}
                        onChange={handleChange}
                      />
                    ) : (
                      <p>{user?.phone || "-"}</p>
                    )}
                  </div>

                  <div className="field field-full">
                    <label>Rol</label>
                    <p>{user?.role || "-"}</p>
                  </div>

                  {isEditing && (
                    <div className="field field-full">
                      <label htmlFor="profile_picture">Foto de Perfil</label>
                      <input
                        id="profile_picture"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </div>
                  )}
                </div>

                <div className="profile-actions">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={handleToggleEdit}
                    disabled={loading}
                  >
                    {isEditing ? "Cancelar" : "Editar Perfil"}
                  </button>

                  {isEditing && (
                    <button
                      type="submit"
                      className="btn-primary btn-sm"
                      disabled={loading}
                    >
                      {loading ? "Guardando..." : "Guardar"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;